import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import api from "@/lib/api";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import { LOGO_URL } from "@/lib/constants";

const Customers = lazy(() => import("@/pages/Customers"));
const Team = lazy(() => import("@/pages/Team"));
const Media = lazy(() => import("@/pages/Media"));
const SocialPlatform = lazy(() => import("@/pages/SocialPlatform"));
const CalendarView = lazy(() => import("@/pages/CalendarView"));
const Settings = lazy(() => import("@/pages/Settings"));
const Products = lazy(() => import("@/pages/Products"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Billing = lazy(() => import("@/pages/Billing"));
const InvoicePrint = lazy(() => import("@/pages/InvoicePrint"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const Purchases = lazy(() => import("@/pages/Purchases"));
const PurchasePrint = lazy(() => import("@/pages/PurchasePrint"));
const Expenses = lazy(() => import("@/pages/Expenses"));
const Proformas = lazy(() => import("@/pages/Proformas"));
const ProformaPrint = lazy(() => import("@/pages/ProformaPrint"));
const Notes = lazy(() => import("@/pages/Notes"));
const NotePrint = lazy(() => import("@/pages/NotePrint"));
const DeliveryChallans = lazy(() => import("@/pages/DeliveryChallans"));
const DeliveryChallanPrint = lazy(() => import("@/pages/DeliveryChallanPrint"));
const EwayBills = lazy(() => import("@/pages/EwayBills"));
const EwayPrint = lazy(() => import("@/pages/EwayPrint"));
const Payments = lazy(() => import("@/pages/Payments"));
const PaymentPrint = lazy(() => import("@/pages/PaymentPrint"));
const Reports = lazy(() => import("@/pages/Reports"));
const Planning = lazy(() => import("@/pages/Planning"));
const AnnualReport = lazy(() => import("@/pages/AnnualReport"));
const BankSpending = lazy(() => import("@/pages/BankSpending"));
const Documents = lazy(() => import("@/pages/Documents"));

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <img src={LOGO_URL} alt="Loading" className="h-12 w-12 rounded-xl object-cover animate-pulse" />
  </div>
);

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = React.useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    (async () => {
      if (!sessionId) { navigate("/login", { replace: true }); return; }
      try {
        const { data } = await api.post("/auth/google", { session_id: sessionId });
        setUser(data);
        window.history.replaceState({}, document.title, "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <img src={LOGO_URL} alt="BEAUTWELLZ" className="h-14 w-14 rounded-xl object-cover animate-pulse" />
      <p className="text-sm text-muted-foreground">Signing you in…</p>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Suspense fallback={<PageFallback />}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/invoice/view/:token" element={<InvoicePrint />} />
      <Route path="/quotation/view/:token" element={<ProformaPrint />} />
      <Route path="/receipt/view/:token" element={<PaymentPrint />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/billing/print/:id" element={<ProtectedRoute><InvoicePrint /></ProtectedRoute>} />
      <Route path="/suppliers" element={<ProtectedRoute><Suppliers /></ProtectedRoute>} />
      <Route path="/purchases" element={<ProtectedRoute><Purchases /></ProtectedRoute>} />
      <Route path="/purchases/print/:id" element={<ProtectedRoute><PurchasePrint /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
      <Route path="/proformas" element={<ProtectedRoute><Proformas /></ProtectedRoute>} />
      <Route path="/proformas/print/:id" element={<ProtectedRoute><ProformaPrint /></ProtectedRoute>} />
      <Route path="/notes" element={<ProtectedRoute><Notes /></ProtectedRoute>} />
      <Route path="/notes/print/:id" element={<ProtectedRoute><NotePrint /></ProtectedRoute>} />
      <Route path="/delivery-challans" element={<ProtectedRoute><DeliveryChallans /></ProtectedRoute>} />
      <Route path="/delivery-challans/print/:id" element={<ProtectedRoute><DeliveryChallanPrint /></ProtectedRoute>} />
      <Route path="/eway-bills" element={<ProtectedRoute><EwayBills /></ProtectedRoute>} />
      <Route path="/eway-bills/print/:id" element={<ProtectedRoute><EwayPrint /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
      <Route path="/payments/print/:id" element={<ProtectedRoute><PaymentPrint /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/planning" element={<ProtectedRoute><Planning /></ProtectedRoute>} />
      <Route path="/annual-report" element={<ProtectedRoute><AnnualReport /></ProtectedRoute>} />
      <Route path="/bank-spending" element={<ProtectedRoute><BankSpending /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
      <Route path="/media" element={<ProtectedRoute><Media /></ProtectedRoute>} />
      <Route path="/social/:platform" element={<ProtectedRoute><SocialPlatform /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarView /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
